import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useRemoteRoleAccess, hasPathAccess } from '@/hooks/useRemoteRoleAccess';
import { useIsApprover } from '@/hooks/approvals/useIsApprover';

import Dashboard from '@/pages/Dashboard';
import SupplierGovernanceDashboard from '@/pages/SupplierGovernanceDashboard';
import QualityIntelligenceDashboard from '@/pages/QualityIntelligenceDashboard';
import ChippieAI from '@/pages/ChippieAI';
import KnowledgeBaseManagement from '@/pages/KnowledgeBaseManagement';
import MCPServerTest from '@/pages/MCPServerTest';
import Farms from '@/pages/Farms';
import FarmForm from '@/pages/FarmForm';
import FarmUpload from '@/pages/FarmUpload';
import FarmDetail from '@/pages/FarmDetail';
import Harvests from '@/pages/Harvests';
import HarvestForm from '@/pages/HarvestForm';
import HarvestCalendar from '@/pages/HarvestCalendar';
import Procurement from '@/pages/Procurement';
import ProcurementFilter from '@/pages/ProcurementFilter';
import ProcurementForm from '@/pages/ProcurementForm';
import ProcurementDetailsPage from '@/pages/ProcurementDetailsPage';
import Suppliers from '@/pages/Suppliers';
import SupplierForm from '@/pages/SupplierForm';
import SourcePerformanceReportPage from '@/pages/suppliers/SourcePerformanceReportPage';
import Agents from '@/pages/Agents';
import AgentForm from '@/pages/AgentForm';
import PriceBenchmarking from '@/pages/PriceBenchmarking';
import PBOverview from '@/pages/price-benchmarking/Overview';
import PBBenchmark from '@/pages/price-benchmarking/Benchmark';
import PBFarmPrices from '@/pages/price-benchmarking/FarmPrices';
import PBDistributionCenter from '@/pages/price-benchmarking/DistributionCenter';
import PBLocalShops from '@/pages/price-benchmarking/LocalShops';
import PBSundayMarket from '@/pages/price-benchmarking/SundayMarket';
import PBSupermarket from '@/pages/price-benchmarking/Supermarket';
import PBEcommerce from '@/pages/price-benchmarking/Ecommerce';
import PBDataEntry from '@/pages/price-benchmarking/DataEntry';
import ChipChipSalesData from '@/pages/ChipChipSalesData';
import ChipChipAcceptedDaily from '@/pages/ChipChipAcceptedDaily';
import B2BSalesData from '@/pages/B2BSalesData';
import B2BCreditCollections from '@/pages/B2BCreditCollections';
import PBRankingsDistributionCenter from '@/pages/price-benchmarking/RankingsDistributionCenter';
import PBRankingsSupermarket from '@/pages/price-benchmarking/RankingsSupermarket';
import PBRankingsOverall from '@/pages/price-benchmarking/RankingsOverall';
import PBRankingsConsistency from '@/pages/price-benchmarking/RankingsConsistency';
import PBRankingsVolume from '@/pages/price-benchmarking/RankingsVolume';
import ManageBenchmarkData from '@/pages/price-benchmarking/ManageBenchmarkData';
import ManageBenchmarkProducts from '@/pages/price-benchmarking/ManageBenchmarkProducts';
import PriceForecast from '@/pages/PriceForecast';
import Finance from '@/pages/Finance';
import FinanceApprovalsPending from '@/pages/finance/FinanceApprovalsPending';
import FinanceApprovalsApproved from '@/pages/finance/FinanceApprovalsApproved';
import FinanceApprovalsRejected from '@/pages/finance/FinanceApprovalsRejected';
import FinancePaymentsConfirmation from '@/pages/finance/FinancePaymentsConfirmation';
import FinancePaymentsConfirmed from '@/pages/finance/FinancePaymentsConfirmed';
import FinancePaymentsUpcoming from '@/pages/finance/FinancePaymentsUpcoming';
import FinancePaymentsCalendar from '@/pages/finance/FinancePaymentsCalendar';
import FinanceReports from '@/pages/finance/FinanceReports';
import BusinessUnitPnLPage from '@/pages/finance/BusinessUnitPnLPage';
import FinanceSpendAnalysis from '@/pages/finance/FinanceSpendAnalysis';
import Quality from '@/pages/Quality';
import QualityInspection from '@/pages/QualityInspection';
import QualityFinalGrading from '@/pages/QualityFinalGrading';
import Warehouse from '@/pages/Warehouse';
import WarehouseInspection from '@/pages/WarehouseInspection';
import Reports from '@/pages/Reports';
import UnderwritingDashboard from '@/pages/UnderwritingDashboard';
import ProcurementDamageReportPage from '@/pages/ProcurementDamageReportPage';
import BatchTraceabilityReportPage from '@/pages/BatchTraceabilityReportPage';
import CM1ReportPage from '@/pages/CM1ReportPage';
import Users from '@/pages/Users';
import Settings from '@/pages/Settings';
import Inventory from '@/pages/Inventory';
import InventoryTransactions from '@/pages/InventoryTransactions';
import InventoryProductDetail from '@/pages/InventoryProductDetail';
import InventoryStockCount from '@/pages/InventoryStockCount';
import InventoryDamages from '@/pages/InventoryDamages';
import InventorySyncReport from '@/pages/InventorySyncReport';
import UnmatchedProducts from '@/pages/UnmatchedProducts';

export const ProtectedRoute = () => {
  const { isAuthenticated, loadingAuth, loadingProfile, user, userRole } = useAuth();
  const location = useLocation();
  const { data: remoteAccessConfig, isLoading: loadingAccess } = useRemoteRoleAccess();
  const { data: approverData } = useIsApprover();

  if (loadingAuth) return <div>Loading...</div>;
  if (!isAuthenticated) return <Navigate to="/login" replace />;

  if (loadingProfile && !user) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="flex flex-col items-center space-y-4">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
          <p className="text-muted-foreground">Loading your profile...</p>
        </div>
      </div>
    );
  }

  // Once authenticated and profile loaded, check role-based access
  if (!loadingAccess && userRole) {
    const allowedPaths = remoteAccessConfig ? remoteAccessConfig[userRole] : null;

    if (allowedPaths) {
      const currentPath = location.pathname;

      // Dashboard and root are always allowed as fallbacks
      if (currentPath === '/' || currentPath === '/dashboard') {
        return <Outlet />;
      }

      // Check for dynamic visibility routes (e.g., approvals)
      if (currentPath === '/my-approvals' && approverData?.isApprover) {
        return <Outlet />;
      }

      // Map current path to access ID and check enforcement
      if (!hasPathAccess(allowedPaths, currentPath)) {
        console.warn(`Access denied to ${currentPath} for role ${userRole}. Redirecting to dashboard.`);
        return <Navigate to="/dashboard" replace />;
      }
    }
  }

  return <Outlet />;
};

export const protectedRoutes = [
  { path: '/', element: <Dashboard /> },
  { path: '/dashboard', element: <Dashboard /> },
  { path: '/supplier-governance', element: <SupplierGovernanceDashboard /> },
  { path: '/quality-intelligence', element: <QualityIntelligenceDashboard /> },
  { path: '/chippie-ai', element: <ChippieAI /> },
  { path: '/knowledge-base', element: <KnowledgeBaseManagement /> },
  { path: '/mcp-server-test', element: <MCPServerTest /> },
  { path: '/farms', element: <Farms /> },
  { path: '/farms/new', element: <FarmForm /> },
  { path: '/farm-form', element: <FarmForm /> },
  { path: '/farms/upload', element: <FarmUpload /> },
  { path: '/farms/:id', element: <FarmDetail /> },
  { path: '/harvests', element: <Harvests /> },
  { path: '/harvests/new', element: <HarvestForm /> },
  { path: '/harvests/:id', element: <HarvestForm /> },
  { path: '/harvest-calendar', element: <HarvestCalendar /> },
  { path: '/procurement', element: <Procurement /> },
  { path: '/procurements', element: <Procurement /> },
  { path: '/procurements/filter', element: <ProcurementFilter /> },
  { path: '/procurements/new', element: <ProcurementForm /> },
  { path: '/procurements/:id', element: <ProcurementDetailsPage /> },
  { path: '/suppliers', element: <Suppliers /> },
  { path: '/suppliers/new', element: <SupplierForm /> },
  { path: '/suppliers/report', element: <SourcePerformanceReportPage /> },
  { path: '/suppliers/:id', element: <SupplierForm /> },
  { path: '/agents', element: <Agents /> },
  { path: '/agents/new', element: <AgentForm /> },
  { path: '/agents/:id', element: <AgentForm /> },
  { path: '/price-benchmarking', element: <PriceBenchmarking /> },
  { path: '/price-benchmarking/overview', element: <PBOverview /> },
  { path: '/price-benchmarking/benchmark', element: <PBBenchmark /> },
  { path: '/price-benchmarking/farm-prices', element: <PBFarmPrices /> },
  { path: '/price-benchmarking/distribution-center', element: <PBDistributionCenter /> },
  { path: '/price-benchmarking/local-shops', element: <PBLocalShops /> },
  { path: '/price-benchmarking/sunday-market', element: <PBSundayMarket /> },
  { path: '/price-benchmarking/supermarket', element: <PBSupermarket /> },
  { path: '/price-benchmarking/ecommerce', element: <PBEcommerce /> },
  { path: '/price-benchmarking/data-entry', element: <PBDataEntry /> },
  { path: '/price-benchmarking/chipchip-sales-data', element: <ChipChipSalesData /> },
  { path: '/price-benchmarking/chipchip-accepted-daily', element: <ChipChipAcceptedDaily /> },
  { path: '/price-benchmarking/b2b-sales-data', element: <B2BSalesData /> },
  { path: '/price-benchmarking/b2b-credit-collections', element: <B2BCreditCollections /> },
  { path: '/price-benchmarking/rankings-distribution-center', element: <PBRankingsDistributionCenter /> },
  { path: '/price-benchmarking/rankings-supermarket', element: <PBRankingsSupermarket /> },
  { path: '/price-benchmarking/rankings-overall', element: <PBRankingsOverall /> },
  { path: '/price-benchmarking/rankings-consistency', element: <PBRankingsConsistency /> },
  { path: '/price-benchmarking/rankings-volume', element: <PBRankingsVolume /> },
  { path: '/price-benchmarking/manage-data', element: <ManageBenchmarkData /> },
  { path: '/price-benchmarking/manage-products', element: <ManageBenchmarkProducts /> },
  { path: '/price-forecast', element: <PriceForecast /> },
  { path: '/finance', element: <Finance /> },
  { path: '/finance/approvals/pending', element: <FinanceApprovalsPending /> },
  { path: '/finance/approvals/approved', element: <FinanceApprovalsApproved /> },
  { path: '/finance/approvals/rejected', element: <FinanceApprovalsRejected /> },
  { path: '/finance/payments/confirmation', element: <FinancePaymentsConfirmation /> },
  { path: '/finance/payments/confirmed', element: <FinancePaymentsConfirmed /> },
  { path: '/finance/payments/upcoming', element: <FinancePaymentsUpcoming /> },
  { path: '/finance/payments/calendar', element: <FinancePaymentsCalendar /> },
  { path: '/finance/reports', element: <FinanceReports /> },
  { path: '/finance/reports/business-unit-pnl', element: <BusinessUnitPnLPage /> },
  { path: '/finance/spend-analysis', element: <FinanceSpendAnalysis /> },
  { path: '/quality', element: <Quality /> },
  { path: '/quality/inspect/:id', element: <QualityInspection /> },
  { path: '/quality/final-grading/:id', element: <QualityFinalGrading /> },
  { path: '/warehouse', element: <Warehouse /> },
  { path: '/warehouse/inspect/:id', element: <WarehouseInspection /> },
  { path: '/reports', element: <Reports /> },
  { path: '/reports/underwriting', element: <UnderwritingDashboard /> },
  { path: '/reports/procurement-damage', element: <ProcurementDamageReportPage /> },
  { path: '/reports/batch-traceability', element: <BatchTraceabilityReportPage /> },
  { path: '/reports/cm1', element: <CM1ReportPage /> },
  { path: '/users', element: <Users /> },
  { path: '/settings', element: <Settings /> },
  { path: '/inventory', element: <Inventory /> },
  { path: '/inventory/transactions', element: <InventoryTransactions /> },
  { path: '/inventory/product/:productId', element: <InventoryProductDetail /> },
  { path: '/inventory/stock-count', element: <InventoryStockCount /> },
  { path: '/inventory/damages', element: <InventoryDamages /> },
  { path: '/inventory/sync-report', element: <InventorySyncReport /> },
  { path: '/inventory/unmatched-products', element: <UnmatchedProducts /> },
];
